import debug from './console'
const logger = debug.withTag('utils:loadScriptAsync')
export const loadedScripts: Map<string, Promise<void>> = new Map()
/** Shell preloads repeat it, so the browser reuses them for these tags. */
export const SCRIPT_REFERRER_POLICY = 'no-referrer-when-downgrade'

/**
 * Safely and asynchronously loads a script into the document.
 * Can load a script either by URL or by providing raw text content.
 * If the script is already loaded, it returns the existing promise.
 * If the script is not loaded, it creates a new script element and appends it to the document head.
 * The script is marked as loaded once it has been successfully appended to the document.
 * If the script fails to load, it rejects the promise with an error message.
 * @param location - The location to load the script into, either 'head' or 'body'.
 * @param data - An object containing the script's text content, src, and key.
 *               The key is used to identify the script and prevent duplicate loads (used when src is not provided).
 *               The src is the URL of the script to load.
 *               The textContent is the raw text content of the script to load.
 * @param options - Additional options to pass to the script element.
 * @returns
 */
export function loadScriptAsync(
  location: 'head' | 'body',
  data: { textContent?: string; src?: string; key?: string },
  options: Record<string, unknown> = {}
): Promise<void> {
  const scriptKey = data.src || data.key
  if (!scriptKey) {
    throw new Error('Script key or src is required')
  }
  const scriptSrc = data.src
  const scriptTextContent = data.textContent

  if (loadedScripts.has(scriptKey)) {
    return loadedScripts.get(scriptKey)!
  }

  const loading = new Promise<void>((resolve, reject) => {
    if (typeof document === 'undefined') {
      if (typeof useHead === 'function' && location === 'head') {
        return useHead({
          script: [
            {
              ...(scriptSrc ? { src: scriptSrc } : {}),
              ...(scriptTextContent ? { textContent: scriptTextContent } : {}),
              type: 'text/javascript',
              async: true,
              onload: () => {
                resolve()
              },
              onerror: () => {
                loadedScripts.delete(scriptKey)
                logger.error(`Failed to load script: ${scriptKey}`)
                reject(new Error(`Failed to load script: ${scriptKey}`))
              },
              ...options,
              ...(data.key ? { key: data.key } : {}),
            },
          ],
        })
      } else {
        logger.warn(
          'loadScriptAsync is called outside of the browser environment'
        )
        return reject(new Error('Document is not defined'))
      }
    }

    // Check if the script is already in the document (only for src scripts)
    if (scriptSrc) {
      const existingScript = document.querySelector(
        `script[src="${scriptSrc}"]`
      )
      if (existingScript) {
        // If the script is already in the document, resolve immediately
        if (existingScript.hasAttribute('data-loaded')) {
          resolve() // Script is already loaded
          return
        } else {
          logger.warn(
            `loadScriptAsync: Script with src "${scriptSrc}" is already present but not loaded yet. Attaching listeners.`
          )
          // If the script is present but not loaded, attach listeners to the existing script
          // instead of removing it, which causes race conditions and load failures.
          const onLoad = () => {
            existingScript.setAttribute('data-loaded', 'true')
            resolve()
            existingScript.removeEventListener('load', onLoad)
            existingScript.removeEventListener('error', onError)
          }

          const onError = () => {
            existingScript.removeAttribute('data-loaded')
            loadedScripts.delete(scriptKey)
            logger.error(`Failed to load script: ${scriptKey}`)
            reject(new Error(`Failed to load script: ${scriptKey}`))
            existingScript.removeEventListener('load', onLoad)
            existingScript.removeEventListener('error', onError)
          }

          existingScript.addEventListener('load', onLoad)
          existingScript.addEventListener('error', onError)

          if (existingScript.hasAttribute('data-loaded')) {
            onLoad()
          }
          return
        }
      }
    }

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true

    if (scriptSrc) {
      script.src = scriptSrc
      script.referrerPolicy = SCRIPT_REFERRER_POLICY
    } else if (scriptTextContent) {
      script.textContent = scriptTextContent
      resolve()
    }

    script.onload = () => {
      script.setAttribute('data-loaded', 'true')
      resolve()
    }
    script.onerror = () => {
      script.removeAttribute('data-loaded')
      loadedScripts.delete(scriptKey)
      logger.error(`Failed to load script: ${scriptKey}`)
      reject(new Error(`Failed to load script: ${scriptKey}`))
    }

    if (location === 'head') {
      document.head.appendChild(script)
    } else {
      document.body.appendChild(script)
    }
  })
  if (typeof document === 'undefined') {
    // A server render cannot load a script, so this promise can reject before
    // anything awaits it. Observing it once here keeps an unhandled rejection
    // from taking the server process down. Callers that await it still see it.
    loading.catch(() => undefined)
  }
  loadedScripts.set(scriptKey, loading)
  return loading
}

export default loadScriptAsync
